import React, { useEffect, useState, useCallback } from 'react';
import { packingApi, settingsApi } from '../services/api';

const SESSION_KEY = 'psi_packing_auth';

export default function PackingKiosk() {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(SESSION_KEY) === '1');
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const [correctPin, setCorrectPin] = useState(null);

  useEffect(() => {
    settingsApi.get().then(s => setCorrectPin(s.packing_pin || '1234'));
  }, []);

  function handlePin(digit) {
    const next = pin + digit;
    setPin(next);
    setPinError(false);
    if (next.length === 4) {
      if (next === correctPin) {
        sessionStorage.setItem(SESSION_KEY, '1');
        const el = document.documentElement;
        if (el.requestFullscreen) el.requestFullscreen();
        else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
        setAuthed(true);
      } else {
        setPinError(true);
        setTimeout(() => setPin(''), 600);
      }
    }
  }

  function handleBackspace() {
    setPin(p => p.slice(0, -1));
    setPinError(false);
  }

  if (!authed) {
    return (
      <div style={{
        position: 'fixed', inset: 0,
        background: '#0d3a6e',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        color: 'white', gap: 32,
        touchAction: 'manipulation',
      }}>
        <img src="/logo.png" alt="P.S.I." style={{ width: 180, background: 'white', borderRadius: 12, padding: 12 }} />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700 }}>Balírna</div>
          <div style={{ fontSize: 14, opacity: 0.6, marginTop: 4 }}>Zadejte PIN pro přístup</div>
        </div>

        {/* PIN dots */}
        <div style={{ display: 'flex', gap: 16 }}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{
              width: 18, height: 18, borderRadius: '50%',
              background: pinError ? '#A32D2D' : pin.length > i ? 'white' : 'rgba(255,255,255,0.25)',
              transition: 'background 0.15s',
              boxShadow: pinError ? '0 0 0 2px #ff6b6b' : 'none',
            }} />
          ))}
        </div>

        {pinError && (
          <div style={{ color: '#ff8888', fontSize: 14, marginTop: -16 }}>Špatný PIN</div>
        )}

        {/* Numpad */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[1,2,3,4,5,6,7,8,9].map(d => (
            <PinButton key={d} label={String(d)} onClick={() => handlePin(String(d))} />
          ))}
          <div />
          <PinButton label="0" onClick={() => handlePin('0')} />
          <PinButton label="⌫" onClick={handleBackspace} dim />
        </div>
      </div>
    );
  }

  return <PackingView onLogout={() => { sessionStorage.removeItem(SESSION_KEY); setAuthed(false); setPin(''); }} />;
}

function PinButton({ label, onClick, dim }) {
  return (
    <button onClick={onClick} style={{
      width: 76, height: 76,
      borderRadius: '50%',
      border: '2px solid rgba(255,255,255,0.2)',
      background: dim ? 'transparent' : 'rgba(255,255,255,0.1)',
      color: 'white',
      fontSize: label === '⌫' ? 22 : 28,
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'background 0.1s',
      WebkitTapHighlightColor: 'transparent',
    }}
    onTouchStart={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
    onTouchEnd={e => e.currentTarget.style.background = dim ? 'transparent' : 'rgba(255,255,255,0.1)'}
    >
      {label}
    </button>
  );
}

// ── Packing view (same logic as before, standalone) ──────────────────────────
function PackingView({ onLogout }) {
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState({});
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [packing, setPacking] = useState(false);
  const [done, setDone] = useState(false);
  const [showLogout, setShowLogout] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([packingApi.queue(), packingApi.stats()])
      .then(([q, s]) => {
        const sorted = [...q].sort((a, b) => (b.is_paid ? 1 : 0) - (a.is_paid ? 1 : 0));
        setQueue(sorted);
        setStats(s);
        setIdx(0);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const current = queue[idx];
  const total = queue.length;

  async function handlePack() {
    if (!current || packing) return;
    setPacking(true);
    await packingApi.pack(current.id);
    setPacking(false);
    setDone(true);
    setTimeout(() => { setDone(false); load(); }, 900);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: '#0d3a6e',
      display: 'flex', flexDirection: 'column',
      touchAction: 'manipulation',
    }}>
      {/* Top bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px',
        background: 'rgba(0,0,0,0.25)',
        color: 'white', flexShrink: 0,
      }}>
        {/* Long-press to show logout */}
        <div
          onTouchStart={() => { const t = setTimeout(() => setShowLogout(true), 1500); return () => clearTimeout(t); }}
          onClick={() => setShowLogout(true)}
          style={{ opacity: 0.01, width: 44, height: 44, cursor: 'default' }}
        />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 18 }}>📫 Balírna</div>
          <div style={{ fontSize: 12, opacity: 0.65 }}>
            {total === 0 ? 'Vše zabaleno' : `${idx + 1} z ${total}`}
          </div>
        </div>
        <button onClick={load} style={{
          background: 'rgba(255,255,255,0.12)', border: 'none', color: 'white',
          width: 44, height: 44, borderRadius: 10, fontSize: 18, cursor: 'pointer',
        }}>🔄</button>
      </div>

      {/* Progress bar */}
      {total > 0 && (
        <div style={{ height: 5, background: 'rgba(255,255,255,0.1)', flexShrink: 0 }}>
          <div style={{
            height: '100%',
            width: `${((idx + 1) / total) * 100}%`,
            background: '#4da6ff',
            transition: 'width 0.4s',
          }} />
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '14px 14px 0' }}>
        {loading && (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="spinner" style={{ width: 36, height: 36, borderTopColor: 'white', borderColor: 'rgba(255,255,255,0.2)', borderWidth: 4 }} />
          </div>
        )}

        {!loading && total === 0 && (
          <div style={{ height: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', gap: 12 }}>
            <div style={{ fontSize: 72 }}>✅</div>
            <div style={{ fontSize: 26, fontWeight: 800 }}>Vše zabaleno!</div>
            <div style={{ fontSize: 15, opacity: 0.6 }}>Žádné další objednávky.</div>
          </div>
        )}

        {!loading && current && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Status banner */}
            <div style={{
              background: current.is_paid ? '#2d5a0e' : '#6b3a08',
              borderRadius: 14, padding: '12px 18px',
              display: 'flex', alignItems: 'center', gap: 12,
              color: 'white', fontWeight: 700, fontSize: 16,
              boxShadow: `0 0 0 2px ${current.is_paid ? '#4a9e1a' : '#c4620f'}`,
            }}>
              <span style={{ fontSize: 26 }}>{current.is_paid ? '✅' : '⚠️'}</span>
              <span>{current.is_paid ? 'ZAPLACENO' : 'ČEKÁ NA PLATBU'}</span>
            </div>

            {/* Main card */}
            <div style={{ background: 'white', borderRadius: 16, padding: '18px 18px 14px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#0d3a6e', lineHeight: 1.2 }}>
                  {current.customer_name}
                </div>
                <div style={{ fontSize: 12, color: '#999', marginTop: 3 }}>{current.order_number}</div>
              </div>

              {/* Delivery */}
              <div style={{
                background: current.delivery_method === 'post' ? '#e8f0fb' : '#edf7e3',
                borderRadius: 10, padding: '10px 14px', marginBottom: 16,
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <span style={{ fontSize: 28 }}>{current.delivery_method === 'post' ? '📮' : '🏠'}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#0d3a6e' }}>
                    {current.delivery_method === 'post' ? 'Zaslat poštou' : 'Vyzvednutí'}
                  </div>
                  {current.delivery_method === 'post' && current.customer_address && (
                    <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
                      {current.customer_address}, {current.customer_zip} {current.customer_city}
                    </div>
                  )}
                </div>
              </div>

              {/* Items */}
              <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                Co zabalit
              </div>
              {current.items?.map((item, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 0',
                  borderBottom: i < current.items.length - 1 ? '1px solid #f0f0f0' : 'none',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: '#185FA5', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 900, fontSize: 17, flexShrink: 0,
                  }}>{item.quantity}×</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 17, lineHeight: 1.2 }}>{item.product_name}</div>
                    {item.variant && <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>{item.variant}</div>}
                  </div>
                </div>
              ))}
            </div>

            {/* Phone + notes */}
            {(current.customer_phone || current.notes) && (
              <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '12px 16px', color: 'white' }}>
                {current.customer_phone && (
                  <a href={`tel:${current.customer_phone}`} style={{
                    color: '#7ec8ff', fontSize: 17, display: 'flex', alignItems: 'center', gap: 8,
                    textDecoration: 'none', marginBottom: current.notes ? 8 : 0,
                  }}>
                    📱 {current.customer_phone}
                  </a>
                )}
                {current.notes && (
                  <div style={{ fontSize: 14, opacity: 0.8, display: 'flex', gap: 6 }}>
                    <span>💬</span><span>{current.notes}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom buttons */}
      {!loading && current && (
        <div style={{ padding: '14px 14px 24px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button onClick={handlePack} disabled={packing}
            style={{
              background: done ? '#2d5a0e' : current.is_paid ? '#3B6D11' : '#854F0B',
              color: 'white', border: 'none', borderRadius: 18,
              padding: '24px', fontSize: 22, fontWeight: 900,
              cursor: packing ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s',
              transform: packing ? 'scale(0.97)' : 'scale(1)',
              boxShadow: `0 6px 20px rgba(0,0,0,0.35)`,
              letterSpacing: '0.03em',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {done ? '✅  Zabaleno!' : packing ? '⏳  Ukládám...' : '📦  ZABALENO — další'}
          </button>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => idx > 0 && setIdx(i => i - 1)} disabled={idx === 0}
              style={{
                flex: 1, padding: '15px', borderRadius: 14, border: 'none',
                background: 'rgba(255,255,255,0.1)', color: 'white',
                fontSize: 16, fontWeight: 600, cursor: idx === 0 ? 'not-allowed' : 'pointer',
                opacity: idx === 0 ? 0.35 : 1, WebkitTapHighlightColor: 'transparent',
              }}>← Předchozí</button>
            <button onClick={() => idx < total - 1 && setIdx(i => i + 1)} disabled={idx >= total - 1}
              style={{
                flex: 1, padding: '15px', borderRadius: 14, border: 'none',
                background: 'rgba(255,255,255,0.1)', color: 'white',
                fontSize: 16, fontWeight: 600, cursor: idx >= total - 1 ? 'not-allowed' : 'pointer',
                opacity: idx >= total - 1 ? 0.35 : 1, WebkitTapHighlightColor: 'transparent',
              }}>Přeskočit →</button>
          </div>
        </div>
      )}

      {/* Logout confirm */}
      {showLogout && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999,
        }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, textAlign: 'center', width: 280 }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Odhlásit se?</div>
            <div style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>Vrátit se na zadání PIN.</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowLogout(false)} style={{ flex: 1, padding: 12, borderRadius: 8, border: '1px solid #ddd', background: 'white', fontSize: 15, cursor: 'pointer' }}>Zpět</button>
              <button onClick={onLogout} style={{ flex: 1, padding: 12, borderRadius: 8, border: 'none', background: '#A32D2D', color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>Odhlásit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
